import { shouldSaveShares } from "layout/collections/helpers";
import { pick, uniqBy } from "lodash";
import {
  saveCollectionShares,
  updateCollection,
} from "slices/collections/actions";
import { setSelected } from "slices/collections/collectionsSlice";
import {
  selectCanShareCollection,
  selectCollections,
} from "slices/collections/selectors";

import { resolveFlowFiles } from "../pipeline";
import { createFlowStrategy } from "./baseFlowStrategy";

// COLLECTIONS is a flow target with RAG-specific overrides: it merges the
// collection's existing files, saves share changes before initialising, and
// resolves the reindex response into ragDocumentId-keyed uploads.
export const createCollectionsStrategy = (config) => ({
  ...createFlowStrategy(config),

  prepareFiles: ({ selectedFiles, selectedCollection }) =>
    uniqBy(
      (selectedCollection?.files || []).concat(selectedFiles),
      (file) => `${file.content.name}-${file.source}`
    ),

  buildBaseInfo: (files, { selectedCollection }) =>
    pick(selectedCollection ?? {}, ["id", "name"]),

  beforeInitialize: async ({ payload, state, dispatch, selectedCollection }) => {
    if (!selectedCollection) {
      return;
    }

    const dirtyCollection = selectCollections(state).find(
      (c) => c.id === selectedCollection.id
    );
    const canShareCollection = selectCanShareCollection(state);

    let updatedCollection = selectedCollection;
    if (!canShareCollection) {
      updatedCollection = { ...selectedCollection, isShared: false, shares: [] };
      dispatch(setSelected(updatedCollection));
    }

    if (dirtyCollection && shouldSaveShares(dirtyCollection, updatedCollection)) {
      await dispatch(saveCollectionShares(updatedCollection));
    }

    if (updatedCollection?.id) {
      Object.assign(payload, pick(updatedCollection, ["id", "name"]));
    } else if (updatedCollection?.name) {
      payload.name = updatedCollection.name;
    }
  },

  resolveInitResponse: ({ data, dispatch, metadata, files, flowId }) => {
    let formattedCollection = {};
    if (data?.collection) {
      formattedCollection = dispatch(updateCollection(data.collection));
    }

    const result = resolveFlowFiles({
      data,
      responseFiles: formattedCollection.files,
      files,
      isCollectionsTarget: true,
    });

    metadata.collectionId = formattedCollection.id;
    metadata.collectionName = formattedCollection.name;
    metadata.flowId = flowId;

    return result;
  },
});
